import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useBeforeUnload, useBlocker, useNavigate, useParams } from 'react-router-dom';

import { Badge } from '../../components/ui/Badge.js';
import { Button } from '../../components/ui/Button.js';
import { ConfirmModal } from '../../components/ui/ConfirmModal.js';
import { Input } from '../../components/ui/Input.js';
import { SeccionColapsable } from '../../components/ui/SeccionColapsable.js';
import { ToastStack } from '../../components/ui/Toast.js';
import { useApi } from '../../hooks/useApi.js';
import { useToasts } from '../../hooks/useToasts.js';
import { ApiError } from '../../lib/api.js';
import type { DiaFestivoTipo, ListaConFechas } from './types.js';

// Las fechas llegan de la API como ISO ancladas en UTC (ver comentario de
// convención en dias-festivos.service.ts) — se extraen los componentes
// directamente del string, sin pasar por Date/toLocaleDateString (que
// aplicarían la zona horaria del navegador y podrían correr el día).
function fmtFecha(iso: string): string {
  const [y, m, d] = iso.slice(0, 10).split('-');
  return `${d}/${m}/${y}`;
}

function errorMessage(err: unknown, fallback: string): string {
  return err instanceof ApiError ? err.message : fallback;
}

// Prompt AD-2 — el botón de basura genérico (.trash-btn) era casi
// imperceptible (gris apagado, ícono de texto pequeño). Ícono SVG propio +
// clase .trash-btn-lg (más grande, rojo por defecto, con borde) en vez del
// emoji 🗑 usado antes.
function TrashIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2m3 0-1 14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2L4 6h16z" />
      <path d="M10 11v6M14 11v6" />
    </svg>
  );
}

const DIAS_SEMANA = [
  { valor: 6, etiqueta: 'Sábado' },
  { valor: 7, etiqueta: 'Domingo' },
] as const;

interface PendingFecha {
  localId:     string;
  fecha:       string;
  descripcion: string;
  tipo:        DiaFestivoTipo;
}

interface FilaFecha {
  key:             string;
  fecha:           string;
  descripcion:     string;
  tipo:            DiaFestivoTipo;
  esNuevaSinGuardar: boolean;
  marcadaEliminar: boolean;
}

// Prompt BK-02 — Paso 3: una tabla por sección (festivos oficiales / fines de
// semana), cada una con su propio checkbox "seleccionar todos" e
// indeterminate — pero comparten el `Set` de seleccionadas del padre, así el
// botón "Eliminar seleccionadas" sigue operando sobre ambas secciones a la vez.
interface TablaFechasProps {
  titulo:        string;
  filas:         FilaFecha[];
  vacioTexto:    string;
  seleccionadas: Set<string>;
  toggleFila:    (key: string) => void;
  toggleTodasDe: (subset: FilaFecha[], todasYaSeleccionadas: boolean) => void;
}

function TablaFechas({ titulo, filas, vacioTexto, seleccionadas, toggleFila, toggleTodasDe }: TablaFechasProps) {
  const todasSeleccionadas = filas.length > 0 && filas.every(f => seleccionadas.has(f.key));
  const algunaSeleccionada = filas.some(f => seleccionadas.has(f.key));
  const headerCheckboxRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (headerCheckboxRef.current) {
      headerCheckboxRef.current.indeterminate = algunaSeleccionada && !todasSeleccionadas;
    }
  }, [algunaSeleccionada, todasSeleccionadas]);

  return (
    <div>
      <div style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--heading)', marginBottom: 8 }}>
        {titulo} <span style={{ fontWeight: 400, color: 'var(--muted)' }}>({filas.length})</span>
      </div>
      <div className="table-wrap">
        <div className="table-scroll">
          <table className="main-table">
            <thead>
              <tr>
                <th style={{ width: 32 }}>
                  <input
                    ref={headerCheckboxRef}
                    type="checkbox"
                    checked={todasSeleccionadas}
                    onChange={() => toggleTodasDe(filas, todasSeleccionadas)}
                    aria-label={`Seleccionar todos — ${titulo}`}
                  />
                </th>
                <th>Fecha</th>
                <th>Descripción</th>
                <th>Estado</th>
              </tr>
            </thead>
            <tbody>
              {filas.length === 0 ? (
                <tr>
                  <td className="empty-td" colSpan={4}>
                    {vacioTexto}
                  </td>
                </tr>
              ) : (
                filas.map(f => (
                  <tr key={f.key} style={f.marcadaEliminar ? { opacity: 0.6 } : undefined}>
                    <td>
                      <input type="checkbox" checked={seleccionadas.has(f.key)} onChange={() => toggleFila(f.key)} aria-label="Seleccionar fila" />
                    </td>
                    <td style={f.marcadaEliminar ? { textDecoration: 'line-through', color: 'var(--danger)' } : undefined}>
                      {fmtFecha(f.fecha)}
                    </td>
                    <td style={f.marcadaEliminar ? { textDecoration: 'line-through', color: 'var(--danger)' } : undefined}>
                      {f.descripcion}
                    </td>
                    <td>
                      {f.marcadaEliminar && <Badge tone="danger">Por eliminar</Badge>}
                      {f.esNuevaSinGuardar && <Badge tone="neutral">Sin guardar</Badge>}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// Prompt AC-3 — todo cambio de fechas (altas y bajas) vive primero en un
// borrador local (agregarDraft/eliminarIds), sin tocar el backend, hasta que
// se presiona "Guardar". "Cancelar" solo limpia el borrador. Un useBlocker
// (requiere el data router migrado en App.tsx/main.tsx) intercepta cualquier
// intento de cambiar de ruta con cambios sin guardar; useBeforeUnload cubre
// cerrar pestaña/refrescar.
export function DiasFestivosDetalle() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { get, post, delete: del } = useApi();
  const queryClient = useQueryClient();
  const { toasts, showToast, dismiss } = useToasts();

  const esNueva = !id;

  const [nombre, setNombreRaw] = useState('');
  const [anio, setAnioRaw] = useState(new Date().getFullYear() + 1);
  const [fechaInicio, setFechaInicioRaw] = useState('');
  const [fechaFin, setFechaFinRaw] = useState('');
  // Prompt AF-2(RH) — bug real: `hayCambiosSinGuardar` para "nueva" estaba
  // DERIVADO directo de nombre/fechaInicio/fechaFin, nunca se reseteaba tras
  // un guardado exitoso, así que el blocker disparaba "¿Salir sin guardar?"
  // sobre una creación que ya había tenido éxito — mismo síntoma y misma
  // causa raíz que el bug de Tipos de Ausencia (AD-2). Fix idéntico: flag
  // `nuevaDirty` explícito, reseteado con flushSync ANTES de navigate().
  const [nuevaDirty, setNuevaDirty] = useState(false);

  function setNombre(v: string) { setNombreRaw(v); setNuevaDirty(true); }
  function setAnio(v: number) { setAnioRaw(v); setNuevaDirty(true); }
  function setFechaInicio(v: string) { setFechaInicioRaw(v); setNuevaDirty(true); }
  function setFechaFin(v: string) { setFechaFinRaw(v); setNuevaDirty(true); }

  const [diasSeleccionados, setDiasSeleccionados] = useState<Set<number>>(new Set());
  const [fechaOficial, setFechaOficial] = useState('');
  const [descripcionOficial, setDescripcionOficial] = useState('');

  const [agregarDraft, setAgregarDraft] = useState<PendingFecha[]>([]);
  const [eliminarIds, setEliminarIds] = useState<Set<string>>(new Set());
  const [seleccionadas, setSeleccionadas] = useState<Set<string>>(new Set());
  const [guardando, setGuardando] = useState(false);

  const { data: lista, isLoading } = useQuery({
    queryKey: ['dias-festivos-listas', id],
    queryFn: () => get<ListaConFechas>(`/dias-festivos-listas/${id}`),
    enabled: !esNueva,
  });

  const hayCambiosSinGuardar = esNueva
    ? nuevaDirty
    : agregarDraft.length > 0 || eliminarIds.size > 0;

  const blocker = useBlocker(hayCambiosSinGuardar);

  useBeforeUnload(
    useCallback(
      (e: BeforeUnloadEvent) => {
        if (!hayCambiosSinGuardar) return;
        e.preventDefault();
        e.returnValue = '';
      },
      [hayCambiosSinGuardar],
    ),
  );

  const crearListaMutation = useMutation({
    mutationFn: () =>
      post<ListaConFechas>('/dias-festivos-listas', {
        nombre,
        anio,
        fecha_inicio: fechaInicio,
        fecha_fin: fechaFin,
      }),
    onSuccess: nueva => {
      // flushSync fuerza a React a confirmar nuevaDirty=false de forma
      // SÍNCRONA antes del navigate() de abajo — mismo mecanismo que
      // TipoAusenciaDetalle.tsx (AD-2): sin esto, el blocker seguía viendo
      // `nuevaDirty=true` en el mismo tick en que se llamaba a navigate().
      flushSync(() => setNuevaDirty(false));
      queryClient.invalidateQueries({ queryKey: ['dias-festivos-listas'] });
      navigate(`/dias-festivos/${nueva.id}`, { replace: true });
      showToast('success', 'Holiday List creada correctamente');
    },
    onError: err => showToast('error', errorMessage(err, 'No fue posible crear la lista')),
  });

  function handleCrearLista() {
    if (!nombre || !fechaInicio || !fechaFin) {
      showToast('error', 'Nombre, fecha de inicio y fecha de fin son obligatorios');
      return;
    }
    crearListaMutation.mutate();
  }

  function existeFecha(fechaIso: string): boolean {
    const persistida = lista?.fechas.some(f => f.fecha.slice(0, 10) === fechaIso && !eliminarIds.has(f.id));
    const enDraft = agregarDraft.some(d => d.fecha === fechaIso);
    return Boolean(persistida) || enDraft;
  }

  // Si la fecha ya estaba persistida y marcada para eliminar, "agregarla de
  // nuevo" solo la desmarca — evita un alta duplicada que de todas formas
  // fallaría con 409 FECHA_DUPLICADA al Guardar (la fila persistida no se
  // borró de verdad todavía, sigue en la BD).
  function agregarFechaADraft(fechaIso: string, descripcion: string, tipo: DiaFestivoTipo) {
    const persistida = lista?.fechas.find(f => f.fecha.slice(0, 10) === fechaIso);
    if (persistida && eliminarIds.has(persistida.id)) {
      setEliminarIds(prev => {
        const next = new Set(prev);
        next.delete(persistida.id);
        return next;
      });
      return;
    }
    setAgregarDraft(prev => [...prev, { localId: crypto.randomUUID(), fecha: fechaIso, descripcion, tipo }]);
  }

  function handleAgregarFinesDeSemana() {
    if (!lista || diasSeleccionados.size === 0) return;

    const inicio = new Date(lista.fecha_inicio);
    const fin = new Date(lista.fecha_fin);

    for (const cursor = new Date(inicio); cursor <= fin; cursor.setUTCDate(cursor.getUTCDate() + 1)) {
      const isoDia = cursor.toISOString().slice(0, 10);
      const diaSemana = cursor.getUTCDay() === 0 ? 7 : cursor.getUTCDay();
      if (!diasSeleccionados.has(diaSemana) || existeFecha(isoDia)) continue;
      const etiqueta = DIAS_SEMANA.find(d => d.valor === diaSemana)?.etiqueta ?? '';
      agregarFechaADraft(isoDia, etiqueta, 'fin_de_semana');
    }
  }

  function handleAgregarFestivoOficial() {
    if (!fechaOficial || !descripcionOficial) {
      showToast('error', 'Fecha y descripción son obligatorias');
      return;
    }
    if (existeFecha(fechaOficial)) {
      showToast('error', 'Ya existe una fecha registrada para ese día en esta lista');
      return;
    }
    agregarFechaADraft(fechaOficial, descripcionOficial, 'festivo_oficial');
    setFechaOficial('');
    setDescripcionOficial('');
  }

  const filas: FilaFecha[] = useMemo(() => {
    if (!lista) return [];
    const persistidas: FilaFecha[] = lista.fechas.map(f => ({
      key: f.id,
      fecha: f.fecha.slice(0, 10),
      descripcion: f.descripcion,
      tipo: f.tipo,
      esNuevaSinGuardar: false,
      marcadaEliminar: eliminarIds.has(f.id),
    }));
    const nuevas: FilaFecha[] = agregarDraft.map(d => ({
      key: `draft-${d.localId}`,
      fecha: d.fecha,
      descripcion: d.descripcion,
      tipo: d.tipo,
      esNuevaSinGuardar: true,
      marcadaEliminar: false,
    }));
    return [...persistidas, ...nuevas].sort((a, b) => a.fecha.localeCompare(b.fecha));
  }, [lista, agregarDraft, eliminarIds]);

  const totalEfectivo = filas.filter(f => !f.marcadaEliminar).length;

  // Prompt BK-02 — Paso 3: `tipo` ('festivo_oficial'/'fin_de_semana') ya
  // distingue esto en el esquema (agregarFecha/generarFinesDeSemana lo
  // pueblan correctamente, verificado contra BD real) — solo faltaba
  // presentarlo en dos secciones en vez de una tabla mezclada.
  const festivosOficiales = useMemo(() => filas.filter(f => f.tipo === 'festivo_oficial'), [filas]);
  const finesDeSemana = useMemo(() => filas.filter(f => f.tipo === 'fin_de_semana'), [filas]);

  const algunaSeleccionada = filas.some(f => seleccionadas.has(f.key));

  function toggleFila(key: string) {
    setSeleccionadas(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  function toggleTodasDe(subset: FilaFecha[], todasYaSeleccionadas: boolean) {
    setSeleccionadas(prev => {
      const next = new Set(prev);
      for (const f of subset) {
        if (todasYaSeleccionadas) next.delete(f.key);
        else next.add(f.key);
      }
      return next;
    });
  }

  // No dispara ningún DELETE — solo mueve las filas seleccionadas al
  // borrador (quitar del alta pendiente, o marcar la persistida para
  // eliminar). El DELETE real (en lote) ocurre recién en handleGuardar().
  function handleEliminarSeleccionadas() {
    for (const key of seleccionadas) {
      if (key.startsWith('draft-')) {
        const localId = key.slice('draft-'.length);
        setAgregarDraft(prev => prev.filter(d => d.localId !== localId));
      } else {
        setEliminarIds(prev => new Set(prev).add(key));
      }
    }
    setSeleccionadas(new Set());
  }

  async function handleGuardar() {
    if (!id) return;
    setGuardando(true);

    if (eliminarIds.size > 0) {
      try {
        await del(`/dias-festivos-listas/${id}/fechas`, { fechaIds: [...eliminarIds] });
        setEliminarIds(new Set());
      } catch (err) {
        setGuardando(false);
        showToast('error', errorMessage(err, 'No fue posible eliminar las fechas marcadas'));
        return;
      }
    }

    const restantes: PendingFecha[] = [];
    let huboError = false;
    for (const item of agregarDraft) {
      try {
        await post(`/dias-festivos-listas/${id}/fechas`, {
          fecha: item.fecha,
          descripcion: item.descripcion,
          tipo: item.tipo,
        });
      } catch (err) {
        huboError = true;
        restantes.push(item);
        showToast('error', errorMessage(err, `No fue posible agregar la fecha ${fmtFecha(item.fecha)}`));
      }
    }
    setAgregarDraft(restantes);
    setSeleccionadas(new Set());

    await queryClient.invalidateQueries({ queryKey: ['dias-festivos-listas', id] });
    setGuardando(false);
    if (!huboError) showToast('success', 'Cambios guardados correctamente');
  }

  function handleCancelar() {
    setAgregarDraft([]);
    setEliminarIds(new Set());
    setSeleccionadas(new Set());
  }

  if (esNueva) {
    return (
      <div>
        <div style={{ fontSize: 19, fontWeight: 700, color: 'var(--heading)', marginBottom: 20 }}>Nueva Holiday List</div>

        <div className="plat-card" style={{ maxWidth: 520 }}>
          <Input label="Nombre" value={nombre} onChange={e => setNombre(e.target.value)} placeholder="Año 2027" />
          <Input label="Año" type="number" value={anio} onChange={e => setAnio(Number(e.target.value))} />
          <Input label="Desde" type="date" value={fechaInicio} onChange={e => setFechaInicio(e.target.value)} />
          <Input label="Hasta" type="date" value={fechaFin} onChange={e => setFechaFin(e.target.value)} />

          <div style={{ display: 'flex', gap: 10, marginTop: 12 }}>
            <Button onClick={handleCrearLista} disabled={crearListaMutation.isPending}>
              {crearListaMutation.isPending ? 'Creando…' : 'Crear lista'}
            </Button>
            <Button variant="secondary" onClick={() => navigate('/dias-festivos')}>
              Cancelar
            </Button>
          </div>
        </div>

        <ToastStack toasts={toasts} onDismiss={dismiss} />

        <ConfirmModal
          open={blocker.state === 'blocked'}
          title="¿Salir sin guardar?"
          body="Tienes cambios sin guardar. Si sales ahora, se perderán."
          confirmLabel="Salir sin guardar"
          danger
          onConfirm={() => blocker.state === 'blocked' && blocker.proceed()}
          onCancel={() => blocker.state === 'blocked' && blocker.reset()}
        />
      </div>
    );
  }

  if (isLoading || !lista) {
    return <div>Cargando…</div>;
  }

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 20 }}>
        <div style={{ fontSize: 19, fontWeight: 700, color: 'var(--heading)' }}>{lista.nombre}</div>
        <div style={{ display: 'flex', gap: 10 }}>
          <Button variant="secondary" onClick={handleCancelar} disabled={!hayCambiosSinGuardar || guardando}>
            Cancelar
          </Button>
          <Button onClick={handleGuardar} disabled={!hayCambiosSinGuardar || guardando}>
            {guardando ? 'Guardando…' : 'Guardar'}
          </Button>
        </div>
      </div>

      <div className="plat-card" style={{ marginBottom: 20 }}>
        <div style={{ display: 'flex', gap: 40, flexWrap: 'wrap' }}>
          <div>
            <div style={{ fontSize: 12, color: 'var(--muted)' }}>Año</div>
            <div style={{ fontWeight: 600 }}>{lista.anio}</div>
          </div>
          <div>
            <div style={{ fontSize: 12, color: 'var(--muted)' }}>Desde</div>
            <div style={{ fontWeight: 600 }}>{fmtFecha(lista.fecha_inicio)}</div>
          </div>
          <div>
            <div style={{ fontSize: 12, color: 'var(--muted)' }}>Hasta</div>
            <div style={{ fontWeight: 600 }}>{fmtFecha(lista.fecha_fin)}</div>
          </div>
          <div>
            <div style={{ fontSize: 12, color: 'var(--muted)' }}>Total de días festivos</div>
            <div style={{ fontWeight: 600 }}>{totalEfectivo}</div>
          </div>
        </div>
      </div>

      <SeccionColapsable title="Cargar fines de semana" defaultOpen={false}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 18, padding: '4px 0 12px' }}>
          {DIAS_SEMANA.map(d => (
            <label key={d.valor} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13.5 }}>
              <input
                type="checkbox"
                checked={diasSeleccionados.has(d.valor)}
                onChange={e =>
                  setDiasSeleccionados(prev => {
                    const next = new Set(prev);
                    if (e.target.checked) next.add(d.valor);
                    else next.delete(d.valor);
                    return next;
                  })
                }
              />
              {d.etiqueta}
            </label>
          ))}
          <Button size="sm" onClick={handleAgregarFinesDeSemana} disabled={diasSeleccionados.size === 0}>
            Agregar
          </Button>
        </div>
      </SeccionColapsable>

      <SeccionColapsable title="Personalizar festivos" defaultOpen={false}>
        <div style={{ display: 'flex', alignItems: 'flex-end', gap: 12, padding: '4px 0 12px', flexWrap: 'wrap' }}>
          <Input label="Fecha" type="date" value={fechaOficial} onChange={e => setFechaOficial(e.target.value)} />
          <Input label="Descripción" value={descripcionOficial} onChange={e => setDescripcionOficial(e.target.value)} placeholder="Día de la Independencia" />
          <Button size="sm" onClick={handleAgregarFestivoOficial}>
            Agregar
          </Button>
        </div>
      </SeccionColapsable>

      <hr className="section-divider" />

      {algunaSeleccionada && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
          <span style={{ fontSize: 13, color: 'var(--muted)' }}>{seleccionadas.size} seleccionada(s)</span>
          <button
            type="button"
            className="trash-btn-lg"
            aria-label="Eliminar seleccionadas"
            title="Eliminar seleccionadas"
            disabled={guardando}
            onClick={handleEliminarSeleccionadas}
          >
            <TrashIcon />
            Eliminar
          </button>
        </div>
      )}

      <TablaFechas
        titulo="Festivos oficiales"
        filas={festivosOficiales}
        vacioTexto="Sin festivos oficiales cargados"
        seleccionadas={seleccionadas}
        toggleFila={toggleFila}
        toggleTodasDe={toggleTodasDe}
      />

      <div style={{ height: 20 }} />

      <TablaFechas
        titulo="Fines de semana"
        filas={finesDeSemana}
        vacioTexto="Sin fines de semana cargados"
        seleccionadas={seleccionadas}
        toggleFila={toggleFila}
        toggleTodasDe={toggleTodasDe}
      />

      <ToastStack toasts={toasts} onDismiss={dismiss} />

      <ConfirmModal
        open={blocker.state === 'blocked'}
        title="¿Salir sin guardar?"
        body="Tienes cambios sin guardar. Si sales ahora, se perderán."
        confirmLabel="Salir sin guardar"
        danger
        onConfirm={() => blocker.state === 'blocked' && blocker.proceed()}
        onCancel={() => blocker.state === 'blocked' && blocker.reset()}
      />
    </div>
  );
}
